import * as React from 'react';
import { observer } from 'mobx-react';
import { NavigateFunction } from 'react-router-dom';

import { ROUTES } from 'Constants';
import { RefundMethod } from 'Models/AppConfigStore';
import { BookingEntity } from 'Models/Entities';
import { cancelEventBooking } from 'Services/Api/_HumanWritten/BookingService/EventsBookingService';
import alertToast from 'Util/ToastifyUtils';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { GetFormattedDateTime } from 'Views/Components/_HumanWritten/Events/EventCards/EventUtils';
import { CancellationWizard } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/CancellationWizard';
import {
	BookingCancellationInformationDto,
	RefundModel,
	resolveRefundMethod,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/NewCancelBookingModal';

interface CancelEventBookingModalProps {
	booking: BookingEntity;
	refresh: () => void;
	navigate: NavigateFunction;
	onClose: () => void;
}

// Constructs a BookingCancellationInformationDto for the wizard from event-booking-only data.
// Events have no per-leg cutoffs, so bank/credit recommended amounts collapse to the single
// maxRefund value; the wizard's fee/cutoff UI is suppressed by hideCancellationRules.
function buildEventCancellationInfo(booking: BookingEntity): BookingCancellationInformationDto {
	const maxRefund = booking.bookingStatus === 'BOOKED' ? (booking?.bookedSummary?.maxRefund ?? 0) : 0;
	const invoiced = booking.transactionStatus === 'AWAITING' || booking.transactionStatus === 'INVOICED';
	const isUnpaid = booking.transactionStatus === 'UNPAID';
	const discounted = (booking.bookedSummary?.discountAmount ?? 0) > 0;

	const ferryTrip = booking.bookedSummary?.ferryTrip;
	const eventName = ferryTrip?.getEventName?.();
	const displayLabel = stringNotEmpty(eventName) ? eventName : `Event booking #${booking.humanReadableId}`;
	const displaySubtitle =
		ferryTrip?.startDate && ferryTrip?.endDate && ferryTrip?.startTime && ferryTrip?.endTime
			? GetFormattedDateTime(ferryTrip.startDate, ferryTrip.endDate, ferryTrip.startTime, ferryTrip.endTime)
			: undefined;

	return {
		bookings: [
			{
				id: booking.id,
				startStopId: '',
				endStopId: '',
				departureDateTime: null,
				mainBooking: true,
				maximumRefund: maxRefund,
				recommendedRefundAmount: maxRefund,
				bankRecommendedRefundAmount: maxRefund,
				creditRecommendedRefundAmount: maxRefund,
				cancellationFee: 0,
				displayLabel,
				displaySubtitle,
			},
		],
		invoiced,
		isUnpaid,
		discounted,
		maximumRefund: maxRefund,
		recommendedRefundAmount: maxRefund,
		bankRecommendedRefundAmount: maxRefund,
		creditRecommendedRefundAmount: maxRefund,
		cancellationFee: 0,
	};
}

export const CancelEventBookingModal: React.FC<CancelEventBookingModalProps> = observer(
	({ booking, refresh, navigate, onClose }) => {
		const cancellationInfo = React.useMemo(() => buildEventCancellationInfo(booking), [booking]);
		const [submitting, setSubmitting] = React.useState(false);

		const handleConfirm = async (
			refundModel: RefundModel,
			recommendedRefundAmount: number,
		): Promise<boolean | undefined> => {
			if (submitting) return false;
			setSubmitting(true);

			const refundMethod: RefundMethod = resolveRefundMethod(refundModel);

			try {
				const response = await cancelEventBooking({
					bookingId: booking.id,
					cancelReturnBooking: false,
					amountToRefund: refundModel.refundAmount,
					recommendedRefundAmount,
					refundMethod,
					cancellationReasonId: refundModel.cancellationReasonId,
					cancellationReasonDetail: refundModel.cancellationReasonDetail,
				});

				switch (response.status) {
					case 200:
						if (stringIsEmpty(response.data)) {
							alertToast('Failed to cancel event booking', 'error');
							return false;
						}
						navigate(
							ROUTES.BOOKING_CANCELLED_SUCCESS.url(
								response.data,
								booking.user.firstName,
								booking.user.lastName,
								booking.id,
								true,
							),
						);
						onClose();
						return true;

					case 400:
						alertToast('Unable to cancel event booking', 'error');
						return false;

					case 409:
						await refresh();
						alertToast('Event booking has already been cancelled. Refreshing page...', 'error');
						onClose();
						return false;

					default:
						alertToast('An error occurred while cancelling the event booking', 'error');
						return false;
				}
			} catch (error) {
				console.error('Error cancelling event booking:', error);
				alertToast('An error occurred while cancelling the event booking', 'error');
				return false;
			} finally {
				setSubmitting(false);
			}
		};

		return (
			<CancellationWizard
				isOpen
				cancellationInformation={cancellationInfo}
				reasonBookingId={booking.id}
				onClose={onClose}
				onConfirm={handleConfirm}
				skipSelectionStep
				hideCancellationRules
				isFerryBooking={false}
				wizardTitle="Cancel event booking"
			/>
		);
	},
);
